<template>
  <div class="text-center">
    <h4 class="mt-3">{{ $t("studyPlan.studyPlanView.studyPlan") }}</h4>

    <!-- Decreto de Rectoría: oculto hasta que el reporte esté terminado
    <div class="d-flex noprint" style="justify-content: right">
      <b-button
        size="sm"
        variant="primary"
        :disabled="disabled_download_decree"
        @click="downloadRectorateDecree"
      >
        <b-icon icon="file-earmark-word" class="mr-1"></b-icon>
        {{ $t("studyPlan.studyPlanView.downloadRectorateDecree") }}
      </b-button>
    </div>
    -->

    <div v-if="institution && institution.internal_use_id != 'duoc.cl'">
      <div class="d-flex" style="justify-content: right">
        <div class="toggle-types noprint">
          <span class="mr-1 label-content font-weight-bold">
            {{ $t("studyPlan.studyPlanView.showHoursBy") }}:
          </span>
          <span
            class="label-content"
            :class="{
              'font-weight-bold': !view_total_hours,
            }"
            >{{ $t("studyPlan.studyPlanView.week") }}</span
          >
          <b-button
            class="check-type"
            variant="primary"
            @click="() => (view_total_hours = !view_total_hours)"
          >
            <b-icon
              icon="circle-fill"
              class="icon-check"
              :class="{
                'icon-check-active': view_total_hours,
              }"
              scale="0.7"
            ></b-icon>
          </b-button>
          <span
            class="label-content"
            :class="{
              'font-weight-bold': view_total_hours,
            }"
            >{{ $t("studyPlan.studyPlanView.total") }}</span
          >
        </div>
      </div>
    </div>
    <b-table-simple class="header-table" bordered responsive outlined>
      <b-thead>
        <b-tr>
          <b-th>{{
            $getVisibleNames("mesh.career", false, "Programa").toUpperCase()
          }}</b-th>
          <b-th class="program-section">
            <div v-if="career">{{ career.name.toUpperCase() }}</div>
            <div v-else>{{ $t("reusable.common.loading") }}</div>
          </b-th>
        </b-tr>
        <b-tr>
          <b-th>
            {{
              $getVisibleNames(
                "manual.mesh.titulo_profesional",
                false,
                "Título Profesional"
              ).toUpperCase()
            }}
            <!-- TÍTULO PROFESIONAL -->
          </b-th>
          <b-th class="program-section">
            <div v-if="egressProfile">
              {{ egressProfile.professional_title.toUpperCase() }}
            </div>
            <div v-else>{{ $t("reusable.common.loading") }}</div>
          </b-th>
        </b-tr>
        <b-tr
          v-if="
            !(
              this.institution &&
              (this.institution.internal_use_id == 'duoc.cl' ||
                !this.institution.show_academic_degree)
            )
          "
        >
          <b-th>
            {{
              $getVisibleNames(
                "manual.grado_academico",
                false,
                "Grado Académico"
              ).toUpperCase()
            }}
            <!-- GRADO ACADÉMICO -->
          </b-th>
          <b-th class="program-section">
            <div v-if="egressProfile">
              {{ egressProfile.academic_degree.toUpperCase() }}
            </div>
            <div v-else>{{ $t("reusable.common.loading") }}</div>
          </b-th>
        </b-tr>
      </b-thead>
    </b-table-simple>
    <div class="header-p" v-if="egressProfile && egressProfile.comments">
      <div v-if="!allows_crud">
        <p
          v-for="(p, index) in egressProfile.comments.split('\n')"
          :key="index"
        >
          {{ p }}
        </p>
      </div>
      <div v-else-if="can_update" style="text-align: center">
        <ResizeAuto>
          <template v-slot:default="{ resize }">
            <textarea
              @input="resize"
              @focus="resize"
              v-model="egressProfile.comments"
              @blur="updateComment"
            ></textarea>
          </template>
        </ResizeAuto>
      </div>
    </div>
    <div v-if="egressProfile">
      <BiannualLevel
        v-for="semester in egressProfile.semester_amount"
        :key="semester.id"
        :egress_profile_id="egress_profile_id"
        :limit_study_plan="limit_study_plan"
        :view_total_hours="view_total_hours"
        :egress_profile_time_allocations="egress_profile_time_allocations"
        :semester="semester"
        :allows_crud="allows_crud && can_update"
      ></BiannualLevel>
    </div>
    <div class="ml-2" v-if="coursesWrapperList.length > 0">
      <b-table
        class="mt-3"
        :items="coursesWrapperList"
        :fields="courses_wrapper_fields"
        small
        bordered
        noCollapse
        fixed
      >
        <template #cell(name)="row">
          {{ row.item.name.toUpperCase() }}
        </template>
        <template #cell(egress_profile_matters)="row">
          <div class="campus-container">
            <div
              v-for="item in getEgressProfileMatterName(
                row.item.egress_profile_matters
              )"
              :key="item.id"
              class="campus-div"
            >
              {{ item.name }}
            </div>
          </div>
        </template>
      </b-table>
    </div>
    <Summary
      :egress_profile_id="egress_profile_id"
      :limit_study_plan="limit_study_plan"
      :egress_profile_time_allocations="egress_profile_time_allocations"
    ></Summary>

  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { showLoadingAlert, downloadFileFromResponse } from "@/utils/utils";

export default {
  name: "StudyPlan",
  components: {
    BiannualLevel: () => import("@/components/mesh/StudyPlan/BiannualLevel"),
    Summary: () => import("@/components/mesh/StudyPlan/Summary"),
    ResizeAuto: () => import("@/components/reusable/ResizeAuto"),
  },
  props: {
    egress_profile_id: {
      type: Number,
      required: true,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    can_update: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Ids de TimeAllocation que el backend considera "disponibles" para este
      // perfil (incluye los asociados a Ambientes de Aprendizaje aunque tengan
      // 0 horas en todo el perfil). El computed `egress_profile_time_allocations`
      // los filtra además por horas reales cargadas antes de mostrarlos como columna.
      available_time_allocation_ids: [],
      limit_study_plan: null,
      // Variable para controlar como se las horas en el plan de estudio, con valores semanalmente o el total de horas.
      view_total_hours: true,
      disabled_download_decree: false,
    };
  },
  computed: {
    ...mapGetters({
      institution: "getInstitution",
      egressProfiles: names.EGRESS_PROFILES,
      careers: names.CAREERS,
      courses_wrapper: names.COURSES_WRAPPER,
      matters: names.MATTERS,
      egress_profiles_matters: names.EGRESS_PROFILES_MATTERS,
      ep_matter_time_allocations: names.EP_MATTER_TIME_ALLOCATIONS,
      matter_time_allocations: names.MATTER_TIME_ALLOCATIONS,
    }),
    egressProfile() {
      return this.egressProfiles.find((x) => x.id == this.egress_profile_id);
    },
    profileEgressProfileMatters() {
      return this.egress_profiles_matters.filter(
        (x) => x.egress_profile == this.egress_profile_id
      );
    },
    // Columnas de horas a mostrar en el Plan de Estudio: de las disponibles
    // para el perfil (`available_time_allocation_ids`), solo las que tienen
    // horas > 0 cargadas en alguna asignatura (base o de perfil) de este
    // perfil de egreso. Evita mostrar columnas siempre vacías.
    // Duoc mantiene el comportamiento anterior (todas las disponibles, sin
    // filtrar por horas) a pedido explícito, para no alterar su Plan de Estudio.
    egress_profile_time_allocations() {
      if (this.institution && this.institution.internal_use_id == "duoc.cl")
        return this.available_time_allocation_ids;
      const egress_profile_matter_ids = this.profileEgressProfileMatters
        .filter((x) => !x.use_matter_values)
        .map((x) => x.id);
      const matter_ids = this.profileEgressProfileMatters
        .filter((x) => x.use_matter_values)
        .map((x) => x.matter);
      const used_time_allocation_ids = new Set();
      this.ep_matter_time_allocations.forEach((x) => {
        if (
          egress_profile_matter_ids.includes(x.egress_profile_matter) &&
          parseFloat(x.hours) > 0
        )
          used_time_allocation_ids.add(x.time_allocation);
      });
      this.matter_time_allocations.forEach((x) => {
        if (matter_ids.includes(x.matter) && parseFloat(x.hours) > 0)
          used_time_allocation_ids.add(x.time_allocation);
      });
      return this.available_time_allocation_ids.filter((id) =>
        used_time_allocation_ids.has(id)
      );
    },
    career() {
      if (!this.egressProfile) return null;
      return this.careers.find((x) => x.id == this.egressProfile.career);
    },
    coursesWrapperList() {
      return this.courses_wrapper
        .filter((x) => x.egress_profile == this.egress_profile_id)
        .sort(function (a, b) {
          if (a.init_level < b.init_level) return -1;
          if (a.init_level > b.init_level) return 1;
          if (a.end_level < b.end_level) return -1;
          if (a.end_level > b.end_level) return 1;
          return 0;
        });
    },
    courses_wrapper_fields() {
      return [
        {
          key: "name",
          label: String(
            this.$getVisibleNames(
              "mesh.coursewrapper",
              true,
              "Envoltorios de Asignaturas"
            )
          ),
          sortable: true,
          thStyle: {
            background: "var(--kl-color-accent-medium) !important",
            color: "white !important",
            border: "1px solid var(--kl-border-black)",
          },
          tdStyle: { border: "1px solid var(--kl-border-black) !important" },
          thClass: "align-middle",
          tdClass: "text-left",
        },
        {
          key: "init_level",

          label: this.$t("studyPlan.studyPlanView.initialLevel"),
          sortable: true,
          thStyle: {
            background: "var(--kl-color-accent-medium) !important",
            color: "white !important",
            border: "1px solid var(--kl-border-black)",
          },
          tdStyle: { border: "1px solid var(--kl-border-black) !important" },
          thClass: "text-center align-middle",
          tdClass: "text-center",
          display_column: true,
        },
        {
          key: "end_level",

          label: this.$t("studyPlan.studyPlanView.finalLevel"),
          sortable: true,
          thStyle: {
            background: "var(--kl-color-accent-medium) !important",
            color: "white !important",
            border: "1px solid var(--kl-border-black)",
          },
          tdStyle: { border: "1px solid var(--kl-border-black) !important" },
          thClass: "text-center align-middle",
          tdClass: "text-center",
          display_column: true,
        },
        this.institution && this.institution.internal_use_id != 'ipss.cl' 
          ? {
              key: "credits",
              label: this.$getVisibleNames(
                "manual.credits",
                true,
                "Créditos SCT Totales"
              ),
              sortable: true,
              thStyle: {
                background: "var(--kl-color-accent-medium) !important",
                color: "white !important",
                border: "1px solid var(--kl-border-black)",
              },
              tdStyle: { border: "1px solid var(--kl-border-black) !important" },
              thClass: "text-center align-middle",
              tdClass: "text-center",
              display_column: true,
            }
          : {},
        {
          key: "hours",
          label: this.$t("studyPlan.studyPlanView.totalPedagogicalHours"),
          sortable: false,
          thStyle: {
            background: "var(--kl-color-accent-medium) !important",
            color: "white !important",
            border: "1px solid var(--kl-border-black)",
          },
          tdStyle: { border: "1px solid var(--kl-border-black) !important" },
          thClass: "text-center align-middle",
          tdClass: "text-center",
          display_column: true,
        },
        {
          key: "egress_profile_matters",
          label: this.$getVisibleNames(
            "mesh.egressprofilematter",
            true,
            "Asignaturas"
          ),
          sortable: true,
          thStyle: {
            background: "var(--kl-color-accent-medium) !important",
            color: "white !important",
            border: "1px solid var(--kl-border-black)",
          },
          tdStyle: { border: "1px solid var(--kl-border-black) !important" },
          thClass: "align-middle",
          tdClass: "text-left",
        },
      ];
    },
  },
  methods: {
    downloadRectorateDecree() {
      this.disabled_download_decree = true;
      showLoadingAlert(
        this.$t("studyPlan.studyPlanView.generatingWord"),
        this.$t("reusable.common.pleaseWaitFile")
      );
      this.$restful
        .Post(
          `/teaching/word_report_rectorate_decree/`,
          { egress_profile: this.egress_profile_id },
          { responseType: "blob" }
        )
        .then((response) => {
          this.$swal.close();
          downloadFileFromResponse(
            response,
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            () => {
              this.disabled_download_decree = false;
            },
            () => {
              this.disabled_download_decree = false;
              this.$swal.fire(
                this.$t("reusable.common.error"),
                this.$t("reusable.common.downloadError"),
                "error"
              );
            }
          );
        })
        .catch(() => {
          this.disabled_download_decree = false;
          this.$swal.close();
          this.$swal.fire(
            this.$t("reusable.common.error"),
            this.$t("reusable.common.problemGenerating"),
            "error"
          );
        });
    },
    updateComment() {
      let payload = {
        egress_profile_id: this.egressProfile.id,
        item: {
          comments: this.egressProfile.comments,
        },
      };
      this.$store.dispatch(names.PATCH_EGRESS_PROFILE, payload);
    },
    getEgressProfileMatterName(egress_profile_matters) {
      let matters = this.egress_profiles_matters
        .filter((x) => egress_profile_matters.includes(x.id))
        .map((x) => x.matter);
      matters = [...new Set(matters)];
      if (matters.length > 0) {
        const list = this.matters.filter((element) =>
          matters.includes(element.id)
        );
        return list.sort((a, b) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        });
      } else return [];
    },
  },
  watch: {
    egressProfile: function () {
      if (this.egressProfile && !this.career) {
        this.$store.dispatch(names.FETCH_CAREER, this.egressProfile.career);
      }
    },
  },
  created() {
    this.$store.dispatch(names.FETCH_MODALITIES);
    this.$store.dispatch(names.FETCH_REGIMES);
    this.$store.dispatch(names.FETCH_FORMATION_AREAS);
    this.$store.dispatch(names.FETCH_PROFILE_TYPES);
    this.$store
      .dispatch(names.FETCH_EGRESS_PROFILE, this.egress_profile_id)
      .then((response) => {
        this.$store.dispatch(
          "CHECK_NUMBERS_TO_WORDS",
          response.semester_amount
        );
      });
    this.$store.dispatch(names.FETCH_COURSES_WRAPPER, {
      egress_profile_id: this.egress_profile_id,
    });
    this.$restful
      .Get(`/mesh/limit_study_plan/?egress_profile=${this.egress_profile_id}`)
      .then((response) => {
        this.limit_study_plan = response;
      });
    this.$restful
      .Get(
        `/mesh/egress_profile_time_allocations/?egress_profile=${this.egress_profile_id}`
      )
      .then((response) => {
        this.available_time_allocation_ids = response;
        // Columnas en StudyPlan.
        let total_columns = 7;
        if (this.institution && this.institution.internal_use_id != 'ipss.cl' )
          total_columns += 3;
        total_columns += response.length;
        if (total_columns < 8)
          this.$store.commit(names.MUTATE_PRINT_ZOOM_SCALE, 0.95);
        if (total_columns > 8 && total_columns <= 12)
          this.$store.commit(names.MUTATE_PRINT_ZOOM_SCALE, 0.8);
        if (total_columns > 12)
          this.$store.commit(names.MUTATE_PRINT_ZOOM_SCALE, 0.7);
      });
    this.$store.dispatch(
      names.FETCH_STUDY_PLAN_COMMENTS,
      this.egress_profile_id
    );
    this.$store.dispatch(names.FETCH_MATTERS, {
      egress_profile_id: this.egress_profile_id,
    });
    this.$store.dispatch(names.FETCH_EGRESS_PROFILES_MATTERS, {
      egress_profile_id: this.egress_profile_id,
    });
    this.$store.dispatch(names.FETCH_EP_MATTER_TIME_ALLOCATIONS, {
      egress_profile_id: this.egress_profile_id,
    });
    this.$store.dispatch(names.FETCH_MATTER_TIME_ALLOCATIONS, {
      egress_profile_id: this.egress_profile_id,
    });
  },
  mounted() {
    this.$store.commit(names.MUTATE_PRINT_ORIENTATION, true);
  },
  beforeDestroy() {
    this.$store.commit(names.RESET_PRINT_ZOOM_SCALE);
    this.$store.commit(names.RESET_PRINT_ORIENTATION);
  },
};
</script>

<style scoped>
.header-table {
  background-color: var(--kl-color-accent-medium) !important;
}
.header-table th {
  border: 1px solid var(--kl-border-black);
  color: var(--kl-text-on-primary) !important;
  padding: 0.25rem;
  background-color: var(--kl-color-accent-medium) !important;
  text-align: left;
  padding-left: 10px;
}
.table .program-section {
  background-color: var(--kl-fourth-color) !important;
}
.header-p {
  text-align: left;
  font-family: "Helvetica Neue";
  line-height: 1em;
  font-weight: bold;
  font-style: italic;
  margin: auto;
  padding: 0 1em;
  overflow: hidden;
  display: block;
}
textarea {
  width: 100%;
  font-family: "Helvetica Neue";
  line-height: 0.8em;
  font-weight: bold;
  font-style: italic;
  height: 4.5rem;
}
.campus-div {
  background-color: var(--kl-chip-bg);
  color: var(--kl-chip-text);
  border: 1px solid var(--kl-chip-border);
  max-width: fit-content;
  border-radius: var(--kl-radius-pill);
  margin: 0.1em 0.1em;
  padding: var(--kl-chip-padding);
  flex-grow: 1;
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw);
  text-align: center;
}
.campus-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: left;
}
/* Estilos del checkbox */
.toggle-types {
  display: flex;
  float: right;
  padding: 0.3em 1em;
  border-radius: 3px;
}
.label-content {
  font-size: var(--secondary-font-size);
}
.check-type {
  background: #007bff !important;
  margin-inline: 0.7em;
  height: 1.12em;
  width: 2.2em !important;
  border-radius: 10px;
}
.icon-check {
  color: white;
  transform: translate(-69%, -50%);
  transition: 0.2s;
}
.icon-check-active {
  transform: translate(15%, -50%) !important;
}
</style>